/**
 * Lift rules (Akash, 2026-11-02): the read every screen shares, and the writes.
 *
 * THE READ SAYS WHY IT HAS NO ANSWER, because "never show a switch nobody is
 * listening to" is the point of it. A 503 is the fleet not reporting; a 404 is
 * an api-server with no such route, and the screens then draw nothing, so the
 * console rolls out ahead of an older one. A failed read drops the last answer
 * on purpose: an old "Robot use allowed" beside a fleet that has gone quiet is
 * what must not happen.
 *
 * THE SITE'S SWITCH. `enabled: false` is `off`: the screens draw what they drew
 * before lift rules existed. The server answers it before anything that can 503,
 * so a 503 always means on. Not knowing yet, or a failure that says nothing either
 * way, is `loading` and draws nothing too: nobody at a site with it off should see
 * a flash of lift rules, and a site with it on only waits for its first answer.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import {
  closedLiftScopes,
  type ClosedLiftScope,
} from '../../state/lift-rule-waiting';
import {
  createLiftRuleWindow,
  deleteLiftRuleHold,
  deleteLiftRuleWindow,
  fetchLiftRuleOccurrences,
  fetchLiftRules,
  fetchLiftRuleWindows,
  putLiftRuleHold,
  resetLiftRuleBreaker,
  skipLiftRuleOccurrence,
} from '../v1-client';
import { apiErrorCode } from './useFireMode';
import type {
  CreateLiftRuleWindowRequest,
  LiftRuleHoldRequest,
  LiftRuleLiftView,
  LiftRuleOccurrencePage,
  LiftRuleScope,
  LiftRulesView,
  LiftRuleWindow,
  SiteId,
} from '../v1';

const RULES_KEY = 'lift-rules' as const;
const WINDOWS_KEY = 'lift-rule-windows' as const;
const OCCURRENCES_KEY = 'lift-rule-occurrences' as const;

export const LIFT_RULES_NOT_REPORTING = 'lift_rules_not_reporting';
export const LIFT_RULE_CUTS_OFF = 'lift_rule_cuts_off';

export type LiftRulesAnswer =
  | { readonly state: 'loading'; readonly view: null }
  /** The site's admin switch is off. */
  | { readonly state: 'off'; readonly view: null }
  | { readonly state: 'ok'; readonly view: LiftRulesView }
  /** No adapter has reported lately. No switch may be drawn. */
  | { readonly state: 'not_reporting'; readonly view: null }
  /** An api-server without the routes: the screens draw nothing. */
  | { readonly state: 'absent'; readonly view: null }
  | { readonly state: 'error'; readonly view: null };

function httpStatus(err: unknown): number | null {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : null;
}

/** Is this a site with lift rules on, whatever the fleet is doing just now? */
export function liftRulesOn(state: LiftRulesAnswer['state']): boolean {
  return state === 'ok' || state === 'not_reporting' || state === 'error';
}

export function useLiftRules(): LiftRulesAnswer {
  const siteId = useSafeSiteId();
  const query = useQuery<LiftRulesView>({
    queryKey: [RULES_KEY, siteId],
    queryFn: () => fetchLiftRules(siteId as SiteId),
    enabled: Boolean(siteId),
    // A change waits on a ride and reports when it lands: "after Phantas-1's
    // ride" should not outlive the ride by long. With the switch off nothing is
    // moving, and this only has to notice it being turned on.
    refetchInterval: (q) => (q.state.data?.enabled === false && !q.state.error ? 60_000 : 5_000),
    staleTime: 2_000,
    retry: false,
    // A hidden tab stops polling, so a returning reader is never shown the
    // last answer while the next one is on its way for 5 s.
    refetchOnWindowFocus: true,
  });
  if (query.isError) {
    if (httpStatus(query.error) === 404) return { state: 'absent', view: null };
    if (apiErrorCode(query.error) === LIFT_RULES_NOT_REPORTING || httpStatus(query.error) === 503) {
      return { state: 'not_reporting', view: null };
    }
    // Says nothing either way. Only a site known to be on is told it failed.
    if (query.data?.enabled === false) return { state: 'off', view: null };
    return query.data ? { state: 'error', view: null } : { state: 'loading', view: null };
  }
  if (query.data) {
    if (query.data.enabled === false) return { state: 'off', view: null };
    // A proxy's answer that is not ours says nothing about the lifts.
    return Array.isArray(query.data.lifts)
      ? { state: 'ok', view: query.data }
      : { state: 'loading', view: null };
  }
  return { state: 'loading', view: null };
}

/** The scopes closing lanes at this site, for the waiting words. */
export function useClosedLiftScopes(): ClosedLiftScope[] | null {
  return closedLiftScopes(useLiftRules().view);
}

/** The floors a refused write would cut off, when that is why it was refused. */
export function cutOffLevelsOf(err: unknown): string[] | null {
  if (apiErrorCode(err) !== LIFT_RULE_CUTS_OFF) return null;
  const detail = (err as { detail?: { detail?: { levels?: unknown } } }).detail;
  const levels = detail?.detail?.levels;
  return Array.isArray(levels) ? levels.filter((l): l is string => typeof l === 'string') : [];
}

export function useLiftRuleWindows(enabled = true) {
  const siteId = useSafeSiteId();
  return useQuery<LiftRuleWindow[]>({
    queryKey: [WINDOWS_KEY, siteId],
    queryFn: () => fetchLiftRuleWindows(siteId as SiteId),
    enabled: Boolean(siteId) && enabled,
    staleTime: 15_000,
    retry: false,
  });
}

export function useLiftRuleOccurrences(range: { fromMs: number; toMs: number } | null) {
  const siteId = useSafeSiteId();
  return useQuery<LiftRuleOccurrencePage>({
    queryKey: [OCCURRENCES_KEY, siteId, range?.fromMs ?? 0, range?.toMs ?? 0],
    queryFn: () => fetchLiftRuleOccurrences(siteId as SiteId, range!.fromMs, range!.toMs),
    enabled: Boolean(siteId) && range !== null,
    staleTime: 15_000,
    retry: false,
  });
}

function useInvalidate(siteId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [RULES_KEY, siteId] });
    void qc.invalidateQueries({ queryKey: [WINDOWS_KEY, siteId] });
    void qc.invalidateQueries({ queryKey: [OCCURRENCES_KEY, siteId] });
  };
}

export interface LiftRuleTarget {
  readonly lift: string;
  readonly scope: LiftRuleScope;
}

export function useSetLiftRuleHold() {
  const siteId = useSafeSiteId();
  const invalidate = useInvalidate(siteId);
  return useMutation<LiftRuleLiftView, Error, LiftRuleTarget & { body: LiftRuleHoldRequest }>({
    mutationFn: ({ lift, scope, body }) => putLiftRuleHold(siteId as SiteId, lift, scope, body),
    onSuccess: invalidate,
  });
}

export function useReleaseLiftRuleHold() {
  const siteId = useSafeSiteId();
  const invalidate = useInvalidate(siteId);
  return useMutation<LiftRuleLiftView, Error, LiftRuleTarget>({
    mutationFn: ({ lift, scope }) => deleteLiftRuleHold(siteId as SiteId, lift, scope),
    onSuccess: invalidate,
  });
}

export function useResetLiftRuleBreaker() {
  const siteId = useSafeSiteId();
  const invalidate = useInvalidate(siteId);
  return useMutation<LiftRuleLiftView, Error, LiftRuleTarget>({
    mutationFn: ({ lift, scope }) => resetLiftRuleBreaker(siteId as SiteId, lift, scope),
    onSuccess: invalidate,
  });
}

export function useCreateLiftRuleWindow() {
  const siteId = useSafeSiteId();
  const invalidate = useInvalidate(siteId);
  return useMutation<LiftRuleWindow, Error, CreateLiftRuleWindowRequest>({
    mutationFn: (body) => createLiftRuleWindow(siteId as SiteId, body),
    onSuccess: invalidate,
  });
}

export function useDeleteLiftRuleWindow() {
  const siteId = useSafeSiteId();
  const invalidate = useInvalidate(siteId);
  return useMutation<void, Error, string>({
    mutationFn: (windowId) => deleteLiftRuleWindow(siteId as SiteId, windowId),
    onSuccess: invalidate,
  });
}

export function useSkipLiftRuleOccurrence() {
  const siteId = useSafeSiteId();
  const invalidate = useInvalidate(siteId);
  return useMutation<LiftRuleWindow, Error, { windowId: string; startMs: number }>({
    mutationFn: ({ windowId, startMs }) =>
      skipLiftRuleOccurrence(siteId as SiteId, windowId, startMs),
    onSuccess: invalidate,
  });
}
